import React, { useState, Component, useEffect, useRef } from "react";
import { AppBar, Tabs, Tab, Toolbar, Typography, Box, useMediaQuery, Button, Container } from "@mui/material";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { setColorblindMode } from "./Util/MarkedText";
import { applyHomebrew, primeEffectiveCache, getEffective } from "./Util/applyHomebrew";
import applyBuiltInAttachments, { itemNeedsBuiltIns } from "./Util/ApplyBuiltInAttachments";
import attachmentsData from "./data/attachments.json";
import LandingTab from "./Tab0.Landing"
import BackgroundTab from "./Tab1.Background";
import ODMTab from "./Tab2.ObligationDutyMorality";
import SpeciesTab from "./Tab3.Species";
import CareerTab from "./Tab4.CareerSpecialization";
import CharacteristicsTab from "./Tab5.CharacteristicsSkills";
import ForceTab from "./Tab6.ForcePowers";
import MotivationTab from "./Tab7.Motivations";
import EquipmentTab from "./Tab8.Equipment";
import GroupResourceTab from "./Tab9.GroupResource";
import ExtrasTab from "./Tab10.Misc"
import CharacterSheetTab from "./Tab11.CharacterSheet";
import characterSheet from "./data/characterSheet.json";
import skillsData from "./data/skills.json";
import "./styles.css";

const darkTheme = createTheme({
  palette: {
    mode: "dark",
  },
  typography: {
    fontFamily: "Tektur",
  },
});

const lightTheme = createTheme({
  palette: {
    mode: "light",
  },
  typography: {
    fontFamily: "Tektur",
  },
});

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo: errorInfo });
  }

  render() {
    if (this.state.hasError) {
      return (
        <Container>
          <Typography variant="h4">The Hyperdrive motivator has been damaged.</Typography>
          <Typography variant="h5">For support, please report the following error on the Hyperdrive Generator Discord</Typography>

          <Box sx={{
            borderRadius: 1,
            border: '2px solid red',
            padding: 2,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            marginTop: 2,
            marginBottom: 2
          }}>
            <Typography sx={{ wordBreak: 'break-all', overflowWrap: 'break-word', whiteSpace: 'pre-wrap' }}> {/* Apply word-wrap here */}
              {this.state.error && this.state.error.toString()}
              <br />
              {this.state.errorInfo && this.state.errorInfo.componentStack}
            </Typography>
          </Box>

          <Button variant="contained" onClick={() => window.open('https://discord.gg/GyXJZqeUC4', '_blank', 'noopener,noreferrer')}>
            Join the Discord
          </Button>
        </Container >
      );
    }

    return this.props.children;
  }
}

function App() {

  const [appFiles, setAppFiles] = useState(() => {
    const stored = JSON.parse(localStorage.getItem('appFiles') || '{}');
    const homebrewState = JSON.parse(localStorage.getItem('homebrewState') || '{}');
    const result = applyHomebrew(stored, homebrewState);
    primeEffectiveCache(result);
    return result;
  });

  const [character, setCharacter] = useState(() => {
    const skills = appFiles.skills ? JSON.parse(appFiles.skills) : skillsData;
    return {
      ...characterSheet,
      Skills: skills,
    };
  });

  const [activeTab, setActiveTab] = useState(0);
  const [renderedTab, setRenderedTab] = useState(0);
  const [theme, setTheme] = useState(() => {
    const isLight = localStorage.getItem('theme') === 'light';
    if (isLight) document.body.classList.add('light-mode');
    return isLight ? lightTheme : darkTheme;
  });
  const [colorblind, setColorblind] = useState(() => {
    const val = localStorage.getItem('colorblind') === 'true';
    setColorblindMode(val);
    return val;
  });

  useEffect(() => {
    primeEffectiveCache(appFiles);
  }, [appFiles]);

  useEffect(() => {
    const attachments = getEffective('attachments', attachmentsData);
    const update = {};
    ['Armor', 'Weapons', 'Gear'].forEach((cat) => {
      const items = character[cat];
      if (!Array.isArray(items) || !items.some((i) => itemNeedsBuiltIns(i, attachments))) { return; }
      update[cat] = items.map((i) => itemNeedsBuiltIns(i, attachments) ? applyBuiltInAttachments(i, attachments) : i);
    });
    if (Object.keys(update).length) { handleCharacterChange(update); }
  }, [character.Armor, character.Weapons, character.Gear]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setTimeout(() => setRenderedTab(newValue), 0);
  };

  const isXxs = useMediaQuery('(max-width:350px)')

  const handleCharacterChange = (value) => {
    setCharacter({ ...character, ...value });
  };

  if (character.GMGrantMode && character.GMGrantMode !== character.XP) {
    handleCharacterChange({ XP: character.GMGrantMode })
  }

  const handleThemeChange = () => {
    const next = theme === darkTheme ? lightTheme : darkTheme;
    setTheme(next);
    document.body.classList.toggle("light-mode", next === lightTheme);
    localStorage.setItem('theme', next === lightTheme ? 'light' : 'dark');
  };

  const [colorPrinting, setColorPrinting] = useState(false);
  const printPrevDark = useRef(false);

  const handlePrint = (color = false) => {
    if (!color) {
      window.print();
      return;
    }
    printPrevDark.current = theme === darkTheme;
    if (printPrevDark.current) {
      setTheme(lightTheme);
      document.body.classList.add("light-mode");
    }
    document.body.classList.add("print-color");
    setColorPrinting(true);
  };

  useEffect(() => {
    if (!colorPrinting) return;
    if (printPrevDark.current && theme !== lightTheme) return;
    const id = setTimeout(() => {
      window.print();
      document.body.classList.remove("print-color");
      if (printPrevDark.current) {
        setTheme(darkTheme);
        document.body.classList.remove("light-mode");
      }
      setColorPrinting(false);
    }, 200);
    return () => clearTimeout(id);
  }, [colorPrinting, theme]);

  const handleColorblindChange = (val) => {
    setColorblind(val);
    setColorblindMode(val);
    localStorage.setItem('colorblind', val);
  };

  return (
    <ThemeProvider theme={theme}>
      <ErrorBoundary>
        <CssBaseline />
        {colorPrinting && printPrevDark.current && (
          <Box
            className="no-print"
            sx={{
              position: "fixed",
              inset: 0,
              zIndex: 20000,
              backgroundColor: "#121212",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Typography sx={{ color: "#fff" }}>Preparing print…</Typography>
          </Box>
        )}
        <Box sx={{
          display: 'flex',
          overflowX: 'auto',
          justifyContent: 'center',
          '&::-webkit-scrollbar': {
            display: 'none'
          },
          scrollbarWidth: 'none',
        }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            className="no-print"
          >
            <Tab label="Home" wrapped />
            <Tab label="Background" wrapped />
            <Tab label="Obligation/Duty/Morality" wrapped />
            <Tab label="Species" wrapped />
            <Tab label="Career/Specialization" wrapped />
            <Tab label="Characteristics/Skills" wrapped />
            <Tab
              label="Force Powers"
              sx={{
                display: (character.ForceRating && character.ForceRating > 0) ? 'flex' : 'none',
              }}
            />
            <Tab label="Motivations" wrapped />
            <Tab label="Equipment" wrapped />
            <Tab label="Group Resource" wrapped />
            <Tab label="Misc" wrapped />
            <Tab label="Character Sheet" wrapped />
          </Tabs>
        </Box>
        {/* Content for each tab */}
        {
          renderedTab === 0 && (
            <LandingTab
              appFiles={appFiles}
              setAppFiles={setAppFiles}
              theme={theme}
              darkTheme={darkTheme}
              handleThemeChange={handleThemeChange}
              colorblind={colorblind}
              handleColorblindChange={handleColorblindChange}
            />
          )
        }
        {
          renderedTab === 1 && (
            <BackgroundTab
              character={character}
              handleCharacterChange={handleCharacterChange}
              appFiles={appFiles}
            />
          )
        }
        {
          renderedTab === 2 && (
            <ODMTab
              character={character}
              handleCharacterChange={handleCharacterChange}
              appFiles={appFiles}
            />
          )
        }
        {
          renderedTab === 3 && (
            <SpeciesTab
              character={character}
              handleCharacterChange={handleCharacterChange}
              appFiles={appFiles}
            />
          )
        }
        {
          renderedTab === 4 && (
            <CareerTab
              character={character}
              handleCharacterChange={handleCharacterChange}
              appFiles={appFiles}
            />
          )
        }
        {
          renderedTab === 5 && (
            <CharacteristicsTab
              character={character}
              handleCharacterChange={handleCharacterChange}
              appFiles={appFiles}
            />
          )
        }
        {
          renderedTab === 6 && (
            <ForceTab
              character={character}
              handleCharacterChange={handleCharacterChange}
              appFiles={appFiles}
            />
          )
        }
        {
          renderedTab === 7 && (
            <MotivationTab
              character={character}
              handleCharacterChange={handleCharacterChange}
              appFiles={appFiles}
            />
          )
        }
        {
          renderedTab === 8 && (
            <EquipmentTab
              character={character}
              handleCharacterChange={handleCharacterChange}
              appFiles={appFiles}
            />
          )
        }
        {
          renderedTab === 9 && (
            <GroupResourceTab
              character={character}
              handleCharacterChange={handleCharacterChange}
              appFiles={appFiles}
            />
          )
        }
        {
          renderedTab === 10 && (
            <ExtrasTab
              character={character}
              handleCharacterChange={handleCharacterChange}
              appFiles={appFiles}
            />
          )
        }
        {
          renderedTab === 11 && (
            <CharacterSheetTab
              character={character}
              handleCharacterChange={handleCharacterChange}
              appFiles={appFiles}
              handlePrint={handlePrint}
            />
          )
        }
        {!isXxs && activeTab !== 0 && (
          <Box sx={{ height: { xs: '56px', sm: '64px' } }} className="no-print" />
        )}
        {!isXxs && activeTab !== 0 && (
          <AppBar position="fixed" color="primary" sx={{ top: "auto", bottom: 0 }} className="no-print">
            <Toolbar style={{ display: "flex" }}>
              {character.GMGrantMode && <Typography color="error">GM Grant mode is on, all XP expenditures will be ignored.</Typography>}
              <Typography
                color={character.XP < 0 ? "error" : "inherit"}
                variant="subtitle1"
                style={{ padding: "16px" }}
              >
                XP: {character.XP}
              </Typography>
              <Typography
                color={character.Credits < 0 ? "error" : "inherit"}
                variant="subtitle1"
              >
                Credits: {character.Credits}
              </Typography>
            </Toolbar>
          </AppBar>
        )}
      </ErrorBoundary>
    </ThemeProvider >
  );
}
export default App;
